import type { PrV2ReviewResponse, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { PullRequest } from '@/lib/pull-requests/types';
import type { Reviewer } from '@/types/github';

import { MergeableState, MergeStateStatus, PullRequestState } from '@/lib/github/graphql/types';
import { buildPrV2Reviewers } from '@/lib/pr-v2/reviewers';

function mapMergeableState(
  mergeable: boolean | null,
  mergeableState: string | null,
): PullRequest['mergeable'] {
  if (mergeable === true) {
    return MergeableState.Mergeable;
  }

  if (mergeable === false) {
    return MergeableState.Conflicting;
  }

  const normalized = mergeableState?.trim().toUpperCase();
  if (normalized === MergeStateStatus.Dirty) {
    return MergeableState.Conflicting;
  }

  if (
    normalized === MergeStateStatus.Blocked ||
    normalized === MergeStateStatus.Draft ||
    normalized === MergeStateStatus.Behind ||
    normalized === MergeStateStatus.Unstable
  ) {
    return MergeableState.Conflicting;
  }

  if (normalized === MergeStateStatus.Unknown || !normalized) {
    return MergeableState.Unknown;
  }

  return MergeableState.Mergeable;
}

function mapMergeStateStatus(mergeableState: string | null): PullRequest['mergeStateStatus'] {
  const normalized = mergeableState?.trim().toUpperCase();
  switch (normalized) {
    case MergeStateStatus.Behind:
      return MergeStateStatus.Behind;
    case MergeStateStatus.Blocked:
      return MergeStateStatus.Blocked;
    case MergeStateStatus.Clean:
      return MergeStateStatus.Clean;
    case MergeStateStatus.Dirty:
      return MergeStateStatus.Dirty;
    case MergeStateStatus.Draft:
      return MergeStateStatus.Draft;
    case MergeStateStatus.HasHooks:
      return MergeStateStatus.HasHooks;
    case MergeStateStatus.Unstable:
      return MergeStateStatus.Unstable;
    default:
      return MergeStateStatus.Unknown;
  }
}

/**
 * Returns one entry per login, with requested reviewers as REQUESTED_REVIEW.
 *
 * Without the dedupe, a re-requested reviewer would appear twice (a pending
 * entry plus their old review), so the merge dropdown would render duplicate
 * avatars and a stale "changes requested" even after a re-review was asked for.
 */
function buildAdapterReviewers(
  requestedReviewers: PrV2SummaryResponse['pullRequest']['metadataSummary']['reviewers'],
  reviews: PrV2ReviewResponse[],
): Reviewer[] {
  return buildPrV2Reviewers(requestedReviewers, reviews).map((reviewer) => ({
    login: reviewer.login,
    avatarUrl: reviewer.avatarUrl ?? '',
    state: reviewer.state,
    submitted_at: reviewer.submittedAt ?? undefined,
    reviewId: reviewer.reviewId ?? undefined,
  }));
}

export function mapPrV2SummaryToPullRequest(
  summary: PrV2SummaryResponse['pullRequest'],
  owner: string,
  repo: string,
  reviews: PrV2ReviewResponse[] = [],
): PullRequest {
  const metadata = summary.metadataSummary;
  const now = summary.githubUpdatedAt ?? summary.cachedAt;
  const normalizedState = summary.state.trim().toUpperCase();
  const state = summary.merged
    ? PullRequestState.Merged
    : normalizedState === PullRequestState.Open
      ? PullRequestState.Open
      : PullRequestState.Closed;

  return {
    nodeId: summary.id,
    number: summary.number,
    title: summary.title ?? `PR #${summary.number}`,
    author: {
      login: summary.author.login ?? 'unknown',
      avatarUrl: summary.author.avatarUrl ?? '',
    },
    createdAt: metadata.createdAt ?? now,
    updatedAt: metadata.updatedAt ?? now,
    closedAt: normalizedState === PullRequestState.Closed ? now : null,
    mergedAt: summary.merged ? now : null,
    draft: summary.draft,
    url: summary.url ?? `https://github.com/${owner}/${repo}/pull/${summary.number}`,
    body: metadata.body ?? '',
    state,
    changedFiles: metadata.changedFiles ?? 0,
    additions: metadata.additions ?? 0,
    deletions: metadata.deletions ?? 0,
    commits: metadata.commits ?? 0,
    review_comments: metadata.reviewComments ?? 0,
    comments: metadata.comments ?? 0,
    merged: summary.merged,
    mergeable: mapMergeableState(metadata.mergeable, metadata.mergeableState),
    rebaseable: false,
    mergeStateStatus: mapMergeStateStatus(metadata.mergeableState),
    mergedBy: null,
    reviewers: buildAdapterReviewers(metadata.reviewers, reviews),
    repository: {
      owner,
      name: repo,
    },
    head: {
      ref: metadata.headRef ?? summary.headSha.slice(0, 7),
      sha: summary.headSha,
    },
    base: {
      ref: getPrV2BaseLabel(summary),
      sha: summary.baseSha,
    },
    headRef: metadata.headRef ?? summary.headSha.slice(0, 7),
    labels: metadata.labels.map((label) => ({
      name: label.name,
      color: label.color ?? '',
      description: '',
      id: label.name,
      isDefault: false,
      url: '',
    })),
    mergeCommitSha: null,
  };
}

/** The base branch's name, or its short SHA when the summary has no name for it. */
export function getPrV2BaseLabel(summary: PrV2SummaryResponse['pullRequest']): string {
  return summary.metadataSummary.baseRef ?? summary.baseSha.slice(0, 7);
}
